<script setup lang="ts">
import type { IService } from '@types'
import { getServiceImages } from '@booking/utils/images'
import { usePriceFormatter } from '@booking/utils/prices'
import { getMinPriceByService } from '@utils/priceLibrary/servicePriceSettingsUtils'
import ServiceTimeDuration from './service/ServiceTimeDuration.vue'

const props = withDefaults(
  defineProps<{
    service?: IService
    defaultImage?: string
    showServiceDuration?: boolean
    showServicePrice?: boolean
  }>(),
  {
    service: () => ({}) as IService,
    defaultImage: '',
    showServiceDuration: true,
    showServicePrice: true,
  },
)

defineEmits(['open:images'])
const serviceUrl = computed(() => (props.service.id ? `/services/${props.service.id}` : ''))
const bookingUrl = computed(() => (props.service.id ? `/booking/${props.service.id}` : ''))
const images = computed(() => getServiceImages(props.service))

const { formatPrice } = usePriceFormatter()
</script>

<template lang="pug">
.service-card.flex.col-12.md_row-16(data-id="service-card" :data-service-id="service.id")
  .details.flex.row-16.md_contents
    UiImage.service-image.s-96(class="md_br-20 md_s-104" :src="images[0]" :zoom="!!images[0]" :showZoom="false" :placeholder="defaultImage" @click="images[0] && $emit('open:images', images)")
    .card-title.flex.col-4.overflow-hidden.min-w-0
      span.title.typ-14-20-500(class="md_typ-14-18-400") {{ service.title }}
      span.description.typ-12-18-400(class="md_typ-13-17-400") {{ service.addresses?.[0]?.country }}
      .flex.row-8.white-space-nowrap.mobile
        ServiceTimeDuration.time.typ-12-18-500(v-if="showServiceDuration" class="md_typ-14-18-300" :service="service")
        .dot.typ-12-18-500(v-if="showServiceDuration && showServicePrice" class="md_typ-14-18-400") •
        .price.typ-12-18-500(v-if="showServicePrice" class="md_typ-14-18-400")
          | {{ $t('word.from.money.base') }}
          | {{ formatPrice(getMinPriceByService(service)) }}
      NuxtLink.link.typ-14-18-400.desktop(:to="serviceUrl" :aria-label="`${service.title}: ${$t('block.service.row.info.link.text')}`") {{ $t('block.service.row.info.link.text') }}
  .card-price.flex.row-8.white-space-nowrap.desktop(class="lt-md_d-none")
    .flex.row-8
      ServiceTimeDuration.time.typ-12-18-500(v-if="showServiceDuration" class="md_typ-14-18-300" :service="service")
      .dot.typ-12-18-500(v-if="showServiceDuration && showServicePrice" class="md_typ-14-18-400") •
      .price.typ-12-18-500(v-if="showServicePrice" class="md_typ-14-18-400")
        | {{ $t('word.from.money.base') }}
        | {{ formatPrice(getMinPriceByService(service)) }}
    UiButton.desktop(label="block.service.info.page.button.booking.text.base" :variant="ButtonVariant.Secondary" :size="Sizes.Leia" :to="bookingUrl")
  .card-actions.flex.row-16.mobile
    UiButton(label="block.service.row.button.list.info.text" :variant="ButtonVariant.Outline" :size="Sizes.HanSolo" :to="serviceUrl")
    UiButton.flex-1(label="block.service.info.page.button.booking.text.base" :variant="ButtonVariant.Secondary" :size="Sizes.HanSolo" :to="bookingUrl")
</template>

<style scoped>
.title {
  color: var(--cue-content-neutral-primary);
}

.description,
.time {
  color: var(--cue-content-neutral-secondary);
}

.time {
  @screen lt-md {
    color: var(--cue-content-neutral-tertiary);
  }
}

.description {
  --uno: text-ellipsis;
  white-space: unset;
}

.link {
  cursor: pointer;
  text-decoration: none;
}

.dot {
  color: var(--cue-content-neutral-disabled);
}

.price {
  color: var(--cue-content-neutral-primary);
}

.card-title {
  --uno: flex-1;

  @screen md {
    --uno: col-12 flex-shrink-0;
    flex: unset;
  }
}

.card-price {
  --uno: align-items-center;

  @screen md {
    --uno: m-l-auto flex-between-flex-end col-8;
  }
}

.desktop {
  @screen lt-md {
    display: none;
  }
}

.mobile {
  @screen md {
    display: none;
  }
}

.card-actions {
  --uno: w-100p;
  place-content: end;

  @screen md {
    --uno: w-auto;
  }
}
</style>
